import React, { useState, useEffect, useContext } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { useUser } from "../../hook/useUser";
import { useAllUsers } from "../../hook/useAllUsers.js";
import { useAllCompany } from "../../hook/useAllCompany.js";
import { useProjectStatus } from "../../hook/useProjectStatus.js";
import { getAdminProjectsDataApi } from "../../api/project";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import MainTable from "../../component/table/MainTable.js";
import SelectComponent from "../../component/SelectComponent.js";
import FullScreenLoading from "../../component/FullScreenLoading.js";
import CreateProjectPopup from "./components/CreateProjectPopup.js";

const tableStyle = {
  height: "calc(100% - 52px)",
  maxHeight: "calc(100% - 52px)",
  ".MuiButtonBase-root.MuiTableSortLabel-root.Mui-active": {
    color: "#fff",
  },
  ".MuiButtonBase-root.MuiTableSortLabel-root:hover": {
    color: "#fff",
  },
  ".MuiTableSortLabel-icon": {
    color: "#fff!important",
    opacity: "1!important",
  },
};

const buttonStyle = {
  pr: 1.5,
  pl: 1,
  py: 0,
  lineHeight: "35px",
  borderRadius: "20px",
};

const UserManage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useUser();
  const allUsers = useAllUsers();
  const allCompany = useAllCompany();
  const projectStatus = useProjectStatus();
  const { showError } = useContext(NotificationContext);

  const projectTableHead = [
    { id: "projectName", label: "Project Name", align: "left" },
    { id: "projectNumber", label: "Project Number", align: "left" },
    {
      id: "plannedStartDate",
      label: "Planned Start Date",
      align: "left",
      type: "date",
    },
    {
      id: "plannedEndDate",
      label: "Planned End Date",
      align: "left",
      type: "date",
    },
    { id: "mainContact", label: "Main Contact Person", align: "left" },
    { id: "projectAdmin", label: "Project Admin", align: "left" },
    { id: "projectContractor", label: "Project Contractor", align: "left" },
    { id: "status", label: "Status", align: "left" },
  ];

  const [state, setState] = useState({
    originalProjectData: [],
    projectData: [],
    mainContactPerOptions: [],
    mainContactPerValue: [],
    contractorOptions: [],
    contractorValue: [],
    statusOptions: [],
    statusValue: [],
    searchValue: "",
    showCreateProject: false,
    isLoading: true,
    isLoadingFullScreen: true,
    page: 0,
    rowsPerPage: 30,
  });

  const updateState = (field, value) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const getProjectsData = async () => {
    try {
      updateState("isLoading", true);
      const result = await getAdminProjectsDataApi();
      if (result.data) {
        const formattedData = result.data.map((item) => {
          const mainContacts = item.mainContactPerson
            ? item.mainContactPerson.map((contact) => `${contact.firstName} ${contact.lastName}`).join(", ")
            : "";
          const projectAdmin = item.projectAdmin ? item.projectAdmin.map((admin) => `${admin.firstName} ${admin.lastName}`).join(", ") : "";
          const projectContractor = item.contractors ? item.contractors.map((contractor) => `${contractor.name}`).join(", ") : "";
          return {
            ...item,
            mainContact: mainContacts,
            projectAdmin: projectAdmin,
            projectContractor: projectContractor,
          };
        });
        updateState("originalProjectData", formattedData);
        updateState("projectData", formattedData);
      }
    } catch {
      showError("Recieved projects data occurred error.");
    } finally {
      updateState("isLoading", false);
    }
  };

  const searchButton = () => {
    updateState("page", 0);
    updateState("isLoading", true);
    const { originalProjectData, searchValue, mainContactPerValue, contractorValue, statusValue } = state;
    const normalizedSearchValue = String(searchValue || "")
      .trim()
      .toLowerCase();
    const statusMapping = Object.fromEntries(projectStatus.map((item) => [item.value, item.label]));
    const mappedStatusValues = statusValue.map((value) => statusMapping[value]);

    let filteredData = [...originalProjectData];

    if (normalizedSearchValue) {
      filteredData = filteredData.filter((project) =>
        Object.values(project).some((value) => {
          if (typeof value === "string" || typeof value === "number") {
            const normalizedValue = String(value).toLowerCase();
            return normalizedValue.includes(normalizedSearchValue);
          }
          return false;
        })
      );
    }

    if (mainContactPerValue.length > 0) {
      filteredData = filteredData.filter((project) => {
        if (!project.mainContactPerson || project.mainContactPerson.length === 0) {
          return true;
        }
        return project.mainContactPerson.some((person) => mainContactPerValue.includes(person.userId));
      });
    }

    if (contractorValue.length > 0) {
      const contractorMapping = Object.fromEntries(allCompany.map((company) => [company.value, company.label]));
      const selectedContractorNames = contractorValue.map((value) => contractorMapping[value]);

      const isAllContractorsSelected = contractorValue.length === state.contractorOptions.length;
      filteredData = filteredData.filter((project) => {
        if (!project.contractors || project.contractors.length === 0) {
          return isAllContractorsSelected;
        }
        return project.contractors.some((contractor) => selectedContractorNames.includes(contractor.name));
      });
    }

    if (mappedStatusValues.length > 0) {
      filteredData = filteredData.filter((project) => mappedStatusValues.includes(project.status));
    }

    updateState("projectData", filteredData);
    setTimeout(() => {
      updateState("isLoading", false);
    }, 100);
  };

  const resetButton = () => {
    updateState("page", 0);
    updateState("isLoading", true);
    const resetState = {
      searchValue: "",
      mainContactPerValue: state.mainContactPerOptions.map((item) => item.value),
      contractorValue: state.contractorOptions.map((item) => item.value),
      statusValue: state.statusOptions.map((item) => item.value),
      projectData: [...state.originalProjectData],
    };
    setState((prevState) => ({
      ...prevState,
      ...resetState,
    }));
    setTimeout(() => {
      updateState("isLoading", false);
    }, 100);
  };

  useEffect(() => {
    if (!allUsers || !allCompany || !projectStatus) return;
    const fetchData = async () => {
      updateState("isLoadingFullScreen", true);
      const formattedOptions = allUsers?.map((item) => ({
        ...item,
        value: item.userId,
        label: item.firstName + " " + item.lastName,
      }));
      updateState("mainContactPerOptions", formattedOptions);
      updateState(
        "mainContactPerValue",
        formattedOptions.map((item) => item.value)
      );
      updateState(
        "contractorOptions",
        allCompany?.filter((c) => c.label !== "CLP")
      );
      updateState(
        "contractorValue",
        allCompany?.filter((c) => c.label !== "CLP").map((item) => item.value)
      );
      updateState(
        "statusOptions",
        projectStatus?.map((item) => ({
          value: item.value,
          label: item.label,
        }))
      );
      updateState(
        "statusValue",
        projectStatus?.map((item) => item.value)
      );
      await getProjectsData();
      updateState("isLoadingFullScreen", false);
    };

    fetchData();
  }, [allUsers, allCompany, projectStatus]);

  return (
    <>
      <Box sx={{ minWidth: 1100 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Project Management
            <img
              src="assets/icons/greyproject.svg"
              alt="Project"
              style={{
                width: "18px",
                height: "22px",
                marginLeft: "8px",
                verticalAlign: "text-top",
              }}
            />
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: "10px", lineHeight: "normal" }}
            onClick={() => {
              navigate("/modules/SystemAdmin");
            }}
          >
            Back
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid #D0D5DE",
            p: 2,
            mt: 1,
            height: "calc(100vh - 144px)",
            borderRadius: "10px",
          }}
        >
          <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
            {(user?.userRole === "System Administrator" || user?.userRole === "Executive User") && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="contained"
                  color="success"
                  sx={{ ...buttonStyle, minWidth: "156px" }}
                  startIcon={<img src="assets/buttons/addproject.svg" alt="Add New Project" />}
                  onClick={() => {
                    updateState("showCreateProject", true);
                  }}
                >
                  Add New Project
                </Button>
              </Box>
            )}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#333",
                  fontWeight: 700,
                }}
              >
                Search
              </Typography>
              <TextField
                placeholder="Search for project infor etc"
                variant="outlined"
                size="small"
                onChange={(e) => {
                  updateState("searchValue", e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchButton();
                  }
                }}
                value={state.searchValue}
                sx={{
                  my: 0.5,
                  width: 160,
                  ".MuiInputBase-inputSizeSmall": {
                    py: "4px!important",
                  },
                }}
              />
            </Box>
            <Box>
              <SelectComponent
                options={state.mainContactPerOptions}
                value={state.mainContactPerValue}
                onChange={(value) => {
                  updateState("mainContactPerValue", value);
                }}
                isMultiSelect
                selectLabel="Main Contact Person"
                selectStyle={{ width: "100%", minWidth: 160, maxWidth: 160 }}
              />
            </Box>
            <Box>
              <SelectComponent
                options={state.contractorOptions}
                value={state.contractorValue}
                onChange={(value) => {
                  updateState("contractorValue", value);
                }}
                isMultiSelect
                selectLabel="Contractor"
                selectStyle={{ width: "100%", minWidth: 160, maxWidth: 160 }}
              />
            </Box>
            <Box>
              <SelectComponent
                options={state.statusOptions}
                value={state.statusValue}
                onChange={(value) => {
                  updateState("statusValue", value);
                }}
                isMultiSelect
                selectLabel="Status"
                selectStyle={{ width: "100%", minWidth: 160, maxWidth: 160 }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "end" }}>
              <Button onClick={searchButton} variant="contained" color="secondary" sx={{ borderRadius: "10px", py: 0.3, mb: 0.5 }}>
                Search
              </Button>
              <Button onClick={resetButton} variant="outlined" color="inherit" sx={{ borderRadius: "10px", py: 0.2, mb: 0.5, ml: 1 }}>
                Reset
              </Button>
            </Box>
          </Box>
          <MainTable
            paperSx={{ flex: 1 }}
            sx={tableStyle}
            columns={projectTableHead}
            rows={state.projectData}
            headerStyles={{
              default: {
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
                color: "#fff",
              },
            }}
            sortable
            sortableColumns={["plannedStartDate", "plannedEndDate"]}
            selectable="single"
            rowClickColor={"#EBFFFE"}
            setSelected={(id) => {
              navigate(`/modules/ProjectDetails/${id[0]}`);
            }}
            showPagination
            page={state.page}
            rowsPerPage={state.rowsPerPage}
            onPageChange={(newPage) => setState((prevState) => ({ ...prevState, page: newPage }))}
            onRowsPerPageChange={(newRowsPerPage) =>
              setState((prevState) => ({
                ...prevState,
                rowsPerPage: newRowsPerPage,
              }))
            }
          />
        </Box>
      </Box>
      <CreateProjectPopup
        open={state.showCreateProject}
        onClose={() => updateState("showCreateProject", false)}
        onSuccess={() => getProjectsData()}
        allUsers={allUsers}
        allCompany={allCompany}
        projectData={state.projectData}
      />
      <FullScreenLoading isLoading={state.isLoadingFullScreen} />
    </>
  );
};

export default UserManage;
